import { MutableRefObject, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { P, match } from 'ts-pattern';

import {
  Button,
  ButtonLink,
  Dialog,
  Flex,
  Heading,
  Notice,
  MenuButton,
  RadioGroup,
  Tabs,
  Tag,
  Text,
  useId,
  useToasts,
  Card,
  DescriptionList,
  GridAlpha,
  Tooltip,
  Box,
  StickyBar,
} from '@applyboard/crystal-ui';
import {
  CheckMarkOutlineIcon,
  ChevronDownOutlineIcon,
  ClockOutlineIcon,
  CloseOutlineIcon,
  ConditionOutlineIcon,
  CopyOutlineIcon,
  DeleteOutlineIcon,
  DownloadOutlineIcon,
  EditOutlineIcon,
  HourglassHalfOutlineIcon,
  MoreVertOutlineIcon,
  ReceiptOutlineIcon,
  ResetOutlineIcon,
} from '@applyboard/ui-icons';
import { copyToClipboard, useTranslation } from '@applyboard/ui-utils';

import {
  canUploadFiles,
  canAnswerQuestions,
  checkIsSinglePaymentFrame,
  DynamicFormPreview,
  isFieldComponent,
  isReceiptComponent,
  canShowFilePreview,
} from '~/components/DynamicForm';
import { DescriptionAndLinks, getRequirementStatusInfo } from '~/components/RequirementCard';
import { getRequirementTitle } from '~/components/RequirementCard/RequirementCard.utils';
import { WomanThinkingIllustration } from '~/illustrations';
import { ApplicationRequirement, type RequirementStatus, type UserAttachment } from '~/types';
import { assert } from '~/utils/assert';
import { canPerformAction, canUpdateReqAttribute } from '~/utils/actions';

import {
  useApplicationPagePermissions,
  useDeleteApplicationRequirementAttachmentForPii,
  useIsNewLayoutEnabled,
  useSendRequirementAction,
  useUpdateApplicationRequirement,
} from '../../../data';
import { useApplicationId } from '../../../data/aggregatedData';
import { canBeEvaluated } from '../RequirementList.utils';
import { RequirementEvaluationTable } from '../RequirementEvaluationTable';
import { ProvideAnswersButton } from './ProvideAnswersButton';
import { UpdateRequirementDialog } from './UpdateRequirementDialog';
import {
  convertResponsesToDynamicForm,
  getAmount,
  getAttachmentComponent,
  getAttachmentDecision,
  getLatestAttachments,
} from './ApplicationRequirementCard.utils';
import { useRequirementListEventTracker } from '../../../utils/eventTracking';
import { useSidebar } from '../../SideBar';
import { GLOBAL_TOP_BAR_HEIGHT, MINI_HEADER_HEIGHT } from '../../../consts';
import {
  DecisionConfirmationDialog,
  type DecisionAction,
  isEvaluationComplete,
} from './DecisionConfirmationDialog';

interface ApplicationRequirementPreviewPanelProps {
  collapseButtonRef?: MutableRefObject<HTMLButtonElement | null>;
  expandButtonRef?: MutableRefObject<HTMLButtonElement | null>;
  id: string;
  requirement?: ApplicationRequirement;
  onOpenChange: (isOpen: boolean) => void;
  onSubmissionSuccess: (requirementId: string) => void;
}
/**
 * Setting a `key` on ApplicationRequirementPreviewPanelContent so React treats
 * it as conceptually different for each requirement to avoid some issues when
 * switching between selected requirements, eg: AWA-5692
 */
export function ApplicationRequirementPreviewPanel(props: ApplicationRequirementPreviewPanelProps) {
  if (!props.requirement) {
    return null;
  }
  return (
    <ApplicationRequirementPreviewPanelContent
      key={props.requirement.id}
      requirement={props.requirement}
      {...props}
    />
  );
}

function ApplicationRequirementPreviewPanelContent({
  collapseButtonRef,
  id,
  expandButtonRef,
  requirement,
  onOpenChange,
  onSubmissionSuccess,
}: Required<Pick<ApplicationRequirementPreviewPanelProps, 'requirement'>> &
  ApplicationRequirementPreviewPanelProps) {
  const [isInvalidEvaluation, setIsInvalidEvaluation] = useState(false);
  const [isEvaluationPending, setIsEvaluationPending] = useState(false);

  const { t } = useTranslation('RequirementList');
  const headingId = useId();
  const evaluationTableHeadingId = useId();

  const { application_id: appId } = useParams<{
    application_id: string;
  }>();

  const { trackEvent } = useRequirementListEventTracker();
  const isAdminRequirement = requirement.classification !== 'applicant_requirement';

  const { status } = requirement;
  const { Icon, intent: tagIntent } = getRequirementStatusInfo({ requirement });

  const formAnswers = requirement?.responses?.answersHistory;

  const hasDraft =
    (status === 'missing' || (status === 'rejected' && !formAnswers?.[0]?.submitted)) &&
    !formAnswers?.[0]?.submitted &&
    requirement.formDefinition.components.some(isFieldComponent);

  const canFulfill =
    requirement.classification === 'applicant_requirement'
      ? canPerformAction(requirement, 'submit')
      : canPerformAction(requirement, 'approve');

  const showDraftSubmissionNotice =
    hasDraft &&
    requirement.classification === 'applicant_requirement' &&
    (canPerformAction(requirement, 'approve') ||
      canPerformAction(requirement, 'reject') ||
      canPerformAction(requirement, 'restart_review'));

  const showSubmitAnswersNotice = hasDraft && !showDraftSubmissionNotice && canFulfill;

  const showAutoSubmittedNotice =
    status === 'reviewing' &&
    formAnswers &&
    formAnswers[0]?.submittedBy === 'applyboard_requirement_synchronization';

  const showInvalidState = isInvalidEvaluation && !isEvaluationComplete(requirement);
  const showAutomationNotice =
    formAnswers && formAnswers[0]?.evaluatedBy === 'applyboard_automation';

  const amount = getAmount(requirement);

  const { setIsSidebarOpen } = useSidebar();

  const containerRef = useRef<HTMLDivElement>(null);

  const isNewLayoutEnabled = useIsNewLayoutEnabled();

  return (
    <aside
      id={id}
      aria-labelledby={headingId}
      css={[
        {
          position: 'relative',
        },
        isNewLayoutEnabled
          ? {
              '@media (min-width: 960px)': {
                height: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT + MINI_HEADER_HEIGHT}px)`,
                overflow: 'auto',
              },
            }
          : {
              height: 'calc(100vh)',
              overflow: 'auto',
            },
      ]}
      ref={containerRef}
    >
      <StickyBar
        sticky={isNewLayoutEnabled ? { xs: false, md: true } : true}
        top={0}
        containerRef={containerRef}
      >
        <Flex direction="column" gap={4} pt={4} pb={2}>
          <Flex align={{ xs: 'stretch', sm: 'center' }} gap={4} justify="end" pt={2} wrap>
            <Flex.Item grow={1}>
              <Tag intent={tagIntent} icon={Icon} size="md">
                {isAdminRequirement && status === 'missing'
                  ? t('requirementStatus.missingAdmin')
                  : t(`requirementStatus.${status}`)}
              </Tag>
            </Flex.Item>
            {requirement.status !== 'inactive' &&
              requirement.formDefinition.components.length > 0 &&
              canFulfill && (
                <ProvideAnswersButton
                  key={requirement.id}
                  requirementId={requirement.id}
                  onOpenChange={onOpenChange}
                  onSubmissionSuccess={onSubmissionSuccess}
                />
              )}
            <ButtonLink
              size="sm"
              aria-controls={id}
              aria-expanded
              ref={collapseButtonRef}
              emphasis="highlighted"
              href={`/applications/${appId}/requirements${window.location.search}`}
              intent="primary"
              leadIcon={CloseOutlineIcon}
              onAfterHistoryPush={() => {
                trackEvent({
                  event: 'Closed details mode',
                  data: {
                    requirementTemplateReferenceNumber: requirement.requirementTemplateNumber,
                  },
                });
                setIsSidebarOpen(true);
                expandButtonRef?.current?.focus();
              }}
            >
              {t('closeDetailsMode')}
            </ButtonLink>
            <ApplicationRequirementPreviewActions
              requirement={requirement}
              isEvaluationPending={isEvaluationPending}
              onInvalidEvaluationChange={setIsInvalidEvaluation}
            />
          </Flex>
          <Heading level={3} id={headingId} variant="headlineS">
            {getRequirementTitle({ requirement })} {amount ? `(${amount})` : ''}
          </Heading>
        </Flex>
      </StickyBar>
      <Box pt={2}>
        <DescriptionAndLinks requirement={requirement} headingSettings={{ baseLevel: 4 }} />
      </Box>
      <Flex direction="column" gap={6} pt={6}>
        {showDraftSubmissionNotice && (
          <Notice intent="warning">{t('draftSubmissionNotice')}</Notice>
        )}
        {showSubmitAnswersNotice && (
          <Notice intent="secondary">
            {t(status === 'rejected' ? 'reviseAnswersNotice' : 'provideAnswersNotice')}
          </Notice>
        )}
        {showAutoSubmittedNotice && <Notice intent="secondary">{t('autoProcessedNotice')}</Notice>}
        {!isAdminRequirement &&
          (status === 'rejected' || status === 'approved' || status === 'reviewing') &&
          formAnswers &&
          formAnswers.length > 0 &&
          !checkIsSinglePaymentFrame(requirement.formDefinition) && (
            <Flex direction="column" gap={2}>
              <Heading level={4} id={evaluationTableHeadingId}>
                {t('requirementEvaluation')}
              </Heading>
              {showAutomationNotice && <Notice intent="secondary">{t('automationNotice')}</Notice>}
              {showInvalidState && (
                <Notice intent="negative">{t('missingEvaluationFields')}</Notice>
              )}
              <RequirementEvaluationTable
                requirement={requirement}
                index={0}
                aria-labelledby={evaluationTableHeadingId}
                showInvalidState={showInvalidState}
                onPendingEvaluationChange={setIsEvaluationPending}
              />
            </Flex>
          )}
        <AnswersHistoryDialogButton requirement={requirement} />
        <LatestUserInputPreview requirement={requirement} />
      </Flex>
    </aside>
  );
}

function ApplicationRequirementPreviewActions({
  isEvaluationPending,
  onInvalidEvaluationChange,
  requirement,
}: {
  isEvaluationPending: boolean;
  requirement: ApplicationRequirement;
  onInvalidEvaluationChange: (isInvalid: boolean) => void;
}) {
  const { t } = useTranslation('ApplicationRequirementCard');
  const [selectedAction, setSelectedAction] = useState<DecisionAction>();
  const { sendRequirementAction, sendRequirementActionStatus } = useSendRequirementAction();
  const actionButtonRef = useRef<HTMLButtonElement | null>(null);
  const decisionButtonRef = useRef<HTMLButtonElement | null>(null);

  const toasts = useToasts();

  const canEvaluate =
    canBeEvaluated(requirement) && canUpdateReqAttribute(requirement, 'answers.decisions');

  return (
    <>
      <Flex gap={2}>
        {canEvaluate && (
          <MakeDecisionButton
            menuButtonRef={decisionButtonRef}
            requirement={requirement}
            onInvalidEvaluationChange={onInvalidEvaluationChange}
            onSelectAction={setSelectedAction}
          />
        )}
        <MoreActionsMenuButton
          menuButtonRef={actionButtonRef}
          onSelectAction={setSelectedAction}
          requirement={requirement}
        />
      </Flex>
      <DecisionConfirmationDialog
        isEvaluationPending={isEvaluationPending}
        isSendingAction={sendRequirementActionStatus === 'loading'}
        onClose={() => {
          flushSync(() => {
            setSelectedAction(undefined);
          });
          if (decisionButtonRef.current) {
            decisionButtonRef.current.focus();
          } else {
            actionButtonRef.current?.focus();
          }
        }}
        onSendAction={action => {
          sendRequirementAction({
            requirementId: requirement.id,
            action,
            options: {
              onSuccess: () => {
                toasts.positive(t(`${action}.success`, { requirementTitle: requirement.title }));
                flushSync(() => {
                  setSelectedAction(undefined);
                });
                if (decisionButtonRef.current) {
                  decisionButtonRef.current.focus();
                } else {
                  actionButtonRef.current?.focus();
                }
              },
              onError: error => {
                if (error instanceof Error) {
                  toasts.negative(error);
                }
              },
            },
          });
        }}
        requirementId={requirement.id}
        selectedAction={selectedAction}
      />
    </>
  );
}

function MakeDecisionButton({
  menuButtonRef,
  requirement,
  onInvalidEvaluationChange,
  onSelectAction,
}: {
  menuButtonRef: MutableRefObject<HTMLButtonElement | null>;
  requirement: ApplicationRequirement;
  onInvalidEvaluationChange: (isInvalid: boolean) => void;
  onSelectAction: (action: DecisionAction) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('ApplicationRequirementCard');
  const { trackEvent } = useRequirementListEventTracker();

  return (
    <MenuButton
      open={isOpen}
      onOpenChange={open => {
        setIsOpen(open);
        if (open) {
          trackEvent({
            event: 'Review - Clicked make decision',
            data: {
              requirementTemplateReferenceNumber: requirement.requirementTemplateNumber,
            },
          });
        }
      }}
      alignment="right"
    >
      <MenuButton.Trigger>
        <Button
          intent="primary"
          leadIcon={ConditionOutlineIcon}
          trailIcon={ChevronDownOutlineIcon}
          size="sm"
          onClick={() => {
            setIsOpen(true);
          }}
          ref={menuButtonRef}
        >
          {t('makeDecisionButton')}
        </Button>
      </MenuButton.Trigger>
      <MenuButton.Button
        icon={CheckMarkOutlineIcon}
        id={`approveRequirementButton-${requirement.id}`}
        onClick={() => {
          if (!isEvaluationComplete(requirement)) {
            onInvalidEvaluationChange(true);
            flushSync(() => {
              setIsOpen(false);
            });
            menuButtonRef.current?.focus();
          } else {
            onInvalidEvaluationChange(false);
            onSelectAction('approve');
            setIsOpen(false);
          }
        }}
      >
        {t('approve.button')}
      </MenuButton.Button>
      <MenuButton.Button
        icon={CloseOutlineIcon}
        id={`rejectRequirementButton-${requirement.id}`}
        onClick={() => {
          if (!isEvaluationComplete(requirement)) {
            onInvalidEvaluationChange(true);
            flushSync(() => {
              setIsOpen(false);
            });
            menuButtonRef.current?.focus();
          } else {
            onInvalidEvaluationChange(false);
            onSelectAction('reject');
            trackEvent({
              event: 'Review - Make decision - Clicked reject requirement',
              data: {
                requirementTemplateReferenceNumber: requirement.requirementTemplateNumber,
              },
            });
            setIsOpen(false);
          }
        }}
      >
        {t('reject.button')}
      </MenuButton.Button>
    </MenuButton>
  );
}

function MoreActionsMenuButton({
  menuButtonRef,
  onSelectAction,
  requirement,
}: {
  menuButtonRef: MutableRefObject<HTMLButtonElement | null>;
  onSelectAction: (action: DecisionAction) => void;
  requirement: ApplicationRequirement;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditRequirementOpen, setIsEditRequirementOpen] = useState(false);
  const { trackEvent } = useRequirementListEventTracker();

  const { t } = useTranslation('ApplicationRequirementCard');
  const applicationId = useApplicationId();
  const toasts = useToasts();

  const isAdminRequirement = requirement.classification !== 'applicant_requirement';

  const canEditRequirement =
    canUpdateReqAttribute(requirement, 'title') && requirement.status !== 'approved';

  const canRestartReview = !isAdminRequirement && canPerformAction(requirement, 'restart_review');
  const showApprove =
    canPerformAction(requirement, 'approve') ||
    (canRestartReview && requirement.status === 'rejected');
  const showReject =
    canPerformAction(requirement, 'reject') ||
    (canRestartReview && requirement.status === 'approved');

  const showDecisionActions =
    !(canBeEvaluated(requirement) && canUpdateReqAttribute(requirement, 'answers.decisions')) &&
    (canRestartReview || showApprove || showReject || !canBeEvaluated(requirement));

  const canUndoSubmit = canPerformAction(requirement, 'undo_submit');
  const undoSubmitBlocked = requirement.availableActions?.some(
    action =>
      action.id === 'undo_submit' &&
      action.additionalInformation?.blockedReason === 'evaluating_requirement',
  );

  return (
    <>
      <MenuButton
        alignment="right"
        open={isOpen}
        onOpenChange={open => {
          if (open) {
            trackEvent({
              event: 'Review - Clicked preview mode actions menu',
              data: {
                requirementTemplateReferenceNumber: requirement.requirementTemplateNumber,
              },
            });
          }
          setIsOpen(open);
        }}
      >
        <MenuButton.Trigger>
          <Button
            leadIcon={MoreVertOutlineIcon}
            emphasis="outlined"
            intent="primary"
            size="sm"
            aria-label={t('moreActionsMenu')}
            onClick={() => setIsOpen(true)}
            ref={menuButtonRef}
          />
        </MenuButton.Trigger>
        {canEditRequirement && (
          <MenuButton.Button
            icon={EditOutlineIcon}
            id={`previewEditRequirement-${requirement.id}`}
            onClick={() => {
              setIsEditRequirementOpen(true);
              setIsOpen(false);
              trackEvent({
                event: 'Review - Preview mode actions menu - Clicked edit requirement',
              });
            }}
          >
            {t('editRequirement.button')}
          </MenuButton.Button>
        )}
        <MenuButton.Button
          icon={CopyOutlineIcon}
          id={`copyRequirementLink-${requirement.id}`}
          onClick={() => {
            const classificationSlug = isAdminRequirement ? 'admin-requirements' : 'requirements';
            copyToClipboard(
              `${window.location.origin}/applications/${applicationId}/${classificationSlug}/${requirement.id}`,
            );
            trackEvent({
              event: 'Review - Preview mode actions menu - Clicked copy requirement link',
            });
            toasts.positive(t('linkCopiedToClipboard'));
            setIsOpen(false);
          }}
        >
          {t('copyRequirementLink')}
        </MenuButton.Button>
        {canUndoSubmit && (
          <MenuButton.Button
            icon={ResetOutlineIcon}
            id={`undoSubmitRequirement-${requirement.id}`}
            onClick={() => {
              onSelectAction('undo_submit');
              trackEvent({
                event: 'Single Application V2 - Applicant Requirements - Clicked Unsubmit',
                data: {
                  undoSubmitBlocked,
                },
              });
              setIsOpen(false);
            }}
          >
            {t('undo_submit.button')}
          </MenuButton.Button>
        )}
        {showDecisionActions && <MenuButton.Divider />}
        {showDecisionActions && showApprove && (
          <MenuButton.Button
            icon={CheckMarkOutlineIcon}
            id={`approveRequirementButton-${requirement.id}`}
            onClick={() => {
              onSelectAction('approve');
              trackEvent({
                event: 'Review - Preview mode actions menu - Clicked approve requirement',
              });
              setIsOpen(false);
            }}
          >
            {t('approve.button')}
          </MenuButton.Button>
        )}
        {showDecisionActions && showReject && (
          <MenuButton.Button
            icon={CloseOutlineIcon}
            id={`rejectRequirementButton-${requirement.id}`}
            onClick={() => {
              onSelectAction('reject');
              trackEvent({
                event: 'Review - Preview mode actions menu - Clicked reject requirement',
              });
              setIsOpen(false);
            }}
          >
            {t('reject.button')}
          </MenuButton.Button>
        )}
        {showDecisionActions && canRestartReview && (
          <MenuButton.Button
            icon={HourglassHalfOutlineIcon}
            id={`makeRequirementReviewingButton-${requirement.id}`}
            onClick={() => {
              onSelectAction('restart_review');
              trackEvent({
                event: 'Review - Preview mode actions menu - Clicked switch back to "in review"',
              });
              setIsOpen(false);
            }}
          >
            {t('restart_review.button')}
          </MenuButton.Button>
        )}
      </MenuButton>
      {isEditRequirementOpen && (
        <UpdateRequirementDialog
          isOpen={isEditRequirementOpen}
          onClose={() => {
            setIsEditRequirementOpen(false);
            menuButtonRef.current?.focus();
          }}
          requirementId={requirement.id}
        />
      )}
    </>
  );
}

function LatestUserInputPreview({ requirement }: { requirement: ApplicationRequirement }) {
  const { t } = useTranslation('ApplicationRequirementCard');

  const isAdminRequirement = requirement.classification !== 'applicant_requirement';
  const hasAnswers = !!requirement.responses?.answersHistory?.length;
  const canFulfill = isAdminRequirement
    ? canPerformAction(requirement, 'approve')
    : canPerformAction(requirement, 'submit');
  const isSubmitted = hasAnswers && requirement.responses?.answersHistory?.[0].submitted;
  const isStatusMissing = requirement.status === 'missing';
  const isSinglePaymentFrame = checkIsSinglePaymentFrame(requirement.formDefinition);

  if (isSinglePaymentFrame && isStatusMissing) {
    return <NoUserInputRequiredState message={t('clickPayMessage')} />;
  }

  if ((!canFulfill && isStatusMissing) || !hasAnswers) {
    if (canAnswerQuestions(requirement.formDefinition)) {
      return <EmptyAnswersState />;
    }
    if (canUploadFiles(requirement.formDefinition)) {
      return <EmptyAttachmentsState />;
    }
  }

  if (hasAnswers && (isSubmitted || !isAdminRequirement)) {
    return (
      <>
        {canAnswerQuestions(requirement.formDefinition) && (
          <QuestionsPreview requirement={requirement} />
        )}
        {canShowFilePreview(requirement.formDefinition) && (
          <AttachmentPreview key={requirement.id} requirement={requirement} />
        )}
      </>
    );
  }

  return <NoUserInputRequiredState />;
}

function AttachmentPreview({ requirement }: { requirement: ApplicationRequirement }) {
  const { t } = useTranslation('ApplicationRequirementCard');
  const { deleteAttachment, isDeletingAttachment } = useDeleteAttachment({ requirement });

  const attachmentsUploaded = getLatestAttachments(requirement);
  const displayedAttachments = attachmentsUploaded.filter(attachment => !attachment?.deleted);

  const [currentTab, setCurrentTab] = useState(
    displayedAttachments?.length ? displayedAttachments[0].id : undefined,
  );

  if (!displayedAttachments || displayedAttachments.length === 0) {
    return <EmptyAttachmentsState />;
  }

  const onlyReceipts =
    requirement.formDefinition.components.some(component => component.type === 'paymentframe') &&
    !requirement.formDefinition.components.some(component => component.type === 'file');

  return (
    <Flex direction="column" gap={4}>
      <Text variant="titleM">{onlyReceipts ? t('receiptPreview') : t('documentPreview')}</Text>
      {displayedAttachments.length > 1 ? (
        <Tabs onChange={setCurrentTab} value={currentTab}>
          {displayedAttachments.map((attachment, index) => {
            return (
              <Tabs.Item key={index} value={attachment.id} label={attachment.name}>
                <div css={theme => ({ marginTop: `-${theme.spacing[4]}` })}>
                  <AttachmentPreviewPanel
                    attachment={attachment}
                    requirement={requirement}
                    onDeleteAttachment={deleteAttachment}
                    isDeletingAttachment={isDeletingAttachment}
                  />
                </div>
              </Tabs.Item>
            );
          })}
        </Tabs>
      ) : (
        <Flex
          as="section"
          direction="column"
          gap={4}
          aria-labelledby={`attachmentPreview-${displayedAttachments[0].id}`}
        >
          <Text id={`attachmentPreview-${displayedAttachments[0].id}`} variant="labelL">
            {displayedAttachments[0].name}
          </Text>
          <div css={theme => ({ borderTop: `1px solid ${theme.colors.borderRegular}` })}>
            <AttachmentPreviewPanel
              attachment={displayedAttachments[0]}
              requirement={requirement}
              onDeleteAttachment={deleteAttachment}
              isDeletingAttachment={isDeletingAttachment}
            />
          </div>
        </Flex>
      )}
    </Flex>
  );
}

function useDeleteAttachment({ requirement }: { requirement: ApplicationRequirement }) {
  const { t } = useTranslation('ApplicationRequirementCard');
  const { setNextUpdate, updateStatus } = useUpdateApplicationRequirement();
  const { deleteAttachmentForPii, isDeletingAttachmentForPii } =
    useDeleteApplicationRequirementAttachmentForPii();
  const toasts = useToasts();
  const isDeletingAttachment = updateStatus === 'loading' || isDeletingAttachmentForPii;
  const attachments = requirement.responses?.attachments;

  const deleteAttachment = ({
    id,
    isPii,
    onSuccess,
  }: {
    id: string;
    isPii?: boolean;
    onSuccess?: () => void;
  }) => {
    const attachment = attachments?.find(attachment => attachment.id === id);

    assert(attachment, `Could not find attachment with id ${id} when deleting`);

    if (isPii) {
      deleteAttachmentForPii(id, {
        onError: error => {
          if (error instanceof Error) {
            toasts.negative(error);
          } else {
            toasts.negative(new Error(t('removeDocument.unknownError')));
          }
        },
        onSuccess: () => {
          toasts.positive(t('removeDocument.success', { documentName: attachment.name }));
          onSuccess?.();
        },
      });
    } else {
      setNextUpdate(
        {
          id: requirement.id,
          type: requirement.type,
          answers: requirement.responses?.answersHistory?.[0].answers.map(answer => ({
            ...answer,
            value: Array.isArray(answer.value)
              ? answer.value.filter(item => item !== id)
              : answer.value,
          })),
        },
        {
          onError: error => toasts.negative(error),
          onSuccess: () => {
            toasts.positive(t('removeDocument.success', { documentName: attachment.name })),
              onSuccess?.();
          },
        },
      );
    }
  };

  return {
    deleteAttachment,
    isDeletingAttachment,
  };
}

function AttachmentPreviewPanel({
  attachment,
  requirement,
  onDeleteAttachment,
  isDeletingAttachment,
}: {
  attachment: UserAttachment;
  requirement: ApplicationRequirement;
  onDeleteAttachment: ({ id, isPiiRemoval }: { id: string; isPiiRemoval?: boolean }) => void;
  isDeletingAttachment?: boolean;
}) {
  const { contentType, downloadLink, name } = attachment;
  const attachmentDecision = getAttachmentDecision(requirement, attachment.id);
  const showAttachmentDecision =
    attachmentDecision && (requirement.status === 'approved' || requirement.status === 'rejected');

  return (
    <Flex direction={{ xs: 'column', lg: 'row' }}>
      {showAttachmentDecision && (
        <>
          <Flex hideBelow="lg">
            <div
              css={theme => ({
                borderRight: `1px solid ${theme.colors.borderRegular}`,
                display: 'flex',
              })}
            >
              <AttachmentDecision
                reason={attachmentDecision.reasonAdditionalNote}
                status={attachmentDecision?.status}
                requirementStatus={requirement.status}
              />
            </div>
          </Flex>
          <Flex hideAbove="lg">
            <div
              css={theme => ({
                borderBottom: `1px solid ${theme.colors.borderRegular}`,
              })}
            >
              <AttachmentDecision
                reason={attachmentDecision.reasonAdditionalNote}
                status={attachmentDecision.status}
                requirementStatus={requirement.status}
              />
            </div>
          </Flex>
        </>
      )}

      <Flex.Item grow={1}>
        <div
          css={theme => ({
            background: theme.colors.bgSecondaryHighlight,
            padding: theme.spacing[6],
          })}
        >
          <Flex gap={6} justify="between">
            {match(contentType)
              .with(P.union('application/pdf', 'text/plain'), () => (
                <div style={{ height: '500px', width: '100%', overflowY: 'auto' }}>
                  <iframe title={name} src={downloadLink} width="100%" height="100%" />
                </div>
              ))
              .with(P.union('image/png', 'image/jpeg', 'image/gif'), () => (
                <div style={{ width: '100%' }}>
                  <img
                    src={downloadLink}
                    alt={name}
                    style={{ display: 'block', maxWidth: '100%' }}
                  />
                </div>
              ))
              .with(P.union('video/mp4', 'video/quicktime'), () => (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video controls width="object-fit">
                  <source title={name} src={downloadLink} type={contentType} />
                </video>
              ))
              .otherwise(() => (
                <NoAttachmentPreviewState />
              ))}
            <AttachmentPreviewActionsButton
              attachment={attachment}
              requirement={requirement}
              onDeleteAttachment={onDeleteAttachment}
              isDeletingAttachment={isDeletingAttachment}
            />
          </Flex>
        </div>
      </Flex.Item>
    </Flex>
  );
}

function AttachmentDecision({
  status = 'no_decision',
  reason,
  requirementStatus,
}: {
  status?: 'approved' | 'rejected' | 'no_decision';
  reason?: string | undefined | null;
  requirementStatus: RequirementStatus;
}) {
  const { t } = useTranslation('RequirementEvaluationTable');

  return (
    <div
      css={{
        width: 'auto',
        '@media(min-width: 1280px)': {
          width: '256px',
        },
      }}
    >
      <Flex direction="column" gap={4} py={6} px={4}>
        <Flex direction="column" gap={2}>
          <Text variant="labelM">{t('evaluationHeader')}</Text>
          {match(status)
            .with('approved', () => (
              <Flex direction="row" gap={2}>
                <CheckMarkOutlineIcon intent="positive" />
                <Text intent="positive">{t('evaluationPass')}</Text>
              </Flex>
            ))
            .with('rejected', () => (
              <Flex direction="row" gap={2}>
                <CloseOutlineIcon intent="negative" />
                <Text intent="negative">{t('evaluationFail')}</Text>
              </Flex>
            ))
            .with('no_decision', () => (
              <Flex direction="row" gap={2}>
                {requirementStatus === 'reviewing' ? (
                  <HourglassHalfOutlineIcon />
                ) : (
                  <ClockOutlineIcon />
                )}
                <Text>{t('evaluationInReview')}</Text>
              </Flex>
            ))
            .exhaustive()}
        </Flex>
        {reason && (
          <Flex direction="column" gap={2}>
            <Text variant="labelM">{t('messageHeader')}</Text>
            <Text>{reason}</Text>
          </Flex>
        )}
      </Flex>
    </div>
  );
}

function NoUserInputRequiredState({ message }: { message?: string }) {
  const { t } = useTranslation('ApplicationRequirementCard');

  return (
    <Flex direction="column" justify="center" align="center" gap={4}>
      <WomanThinkingIllustration />
      <Heading level={4}>{t('noInformationToDisplayHeading')}</Heading>
      <Text>{message || t('doesNotRequireUserInputMessage')}</Text>
    </Flex>
  );
}

function AnswersHistoryDialogButton({ requirement }: { requirement: ApplicationRequirement }) {
  const [isOpen, setIsOpen] = useState(false);
  const firstTabIndex =
    requirement?.responses?.answersHistory?.findIndex(answer => answer.submitted) || 0;
  const [currentTab, setCurrentTab] = useState(firstTabIndex > -1 ? firstTabIndex.toString() : '0');
  const [attachmentToDelete, setAttachmentToDelete] = useState<UserAttachment>();
  const { t } = useTranslation('ApplicationRequirementCard');
  const { trackEvent } = useRequirementListEventTracker();
  const { deleteAttachment, isDeletingAttachment } = useDeleteAttachment({ requirement });
  const { permissions } = useApplicationPagePermissions();

  const answersHistory = requirement.responses?.answersHistory;

  if (
    !answersHistory ||
    answersHistory.length < 1 ||
    !answersHistory[answersHistory.length - 1].submitted
  ) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={newOpen => {
        setIsOpen(newOpen);
        if (!newOpen) {
          trackEvent({
            event: 'Details - Clicked close evaluation history',
          });
        }
      }}
      size="lg"
    >
      <Dialog.Trigger>
        <Button
          intent="primary"
          emphasis="highlighted"
          size="sm"
          onClick={() =>
            trackEvent({
              event: 'Details - Clicked evaluation history',
            })
          }
        >
          {t('seeHistoryButton')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Heading>{t('evaluationHistoryModalTitle')}</Dialog.Heading>
      <Dialog.Content>
        <Tabs onChange={newValue => setCurrentTab(newValue)} value={currentTab}>
          {answersHistory.map((answer, index) => {
            if (!answer.submitted) {
              return null;
            }
            const canDeleteAttachments =
              index > 0 && permissions.destroy_requirement_attachment_for_pii;
            const currentSuffix = index === 0 ? ` (${t('evaluationHistoryModalCurrent')})` : '';

            const evaluatedBy = match(requirement.responses?.answersHistory?.[index]?.evaluatedBy)
              .with(P.nullish, () => undefined)
              .with('applyboard_automation', () =>
                t('evaluationHistoryDetailsCard.evaluatedByAutomation'),
              )
              .with('applyboard_support_team', () =>
                t('evaluationHistoryDetailsCard.evaluatedByApplyboardSupportTeam'),
              )
              .exhaustive();

            const evaluatedTimestamp = requirement.responses?.answersHistory?.[index]?.evaluatedAt;
            const evaluatedAt = evaluatedTimestamp
              ? format(new Date(evaluatedTimestamp).getTime(), 'MMMM d, yyyy h:mm:ss a')
              : undefined;

            const submittedBy = match(requirement.responses?.answersHistory?.[index]?.submittedBy)
              .with(P.nullish, () => undefined)
              .with('applicant', () => t('evaluationHistoryDetailsCard.submittedByApplicant'))
              .with('applyboard_requirement_synchronization', () =>
                t('evaluationHistoryDetailsCard.submittedByApplyboardRequirementSynchronization'),
              )
              .with('applyboard_support_team', () =>
                t('evaluationHistoryDetailsCard.submittedByApplyboardSupportTeam'),
              )
              .exhaustive();

            const submittedTimestamp = requirement.responses?.answersHistory?.[index]?.timestamp;
            const submittedAt = submittedTimestamp
              ? format(new Date(submittedTimestamp).getTime(), 'MMMM d, yyyy h:mm:ss a')
              : undefined;

            const showTooltip =
              requirement.responses?.answersHistory?.[index]?.submittedBy ===
              'applyboard_requirement_synchronization';

            return (
              <Tabs.Item
                key={index.toString()}
                value={index.toString()}
                label={`${format(new Date(answer.timestamp), 'LLL dd, yyyy')}${currentSuffix}`}
              >
                <Card>
                  <DescriptionList variant="free">
                    <GridAlpha columns="auto 1fr" columnGap={4} rowGap={4}>
                      <DescriptionList.Term>
                        {t('evaluationHistoryDetailsCard.submitted')}
                      </DescriptionList.Term>
                      <DescriptionList.Details>
                        <Flex gap={2}>
                          <Text>{[submittedBy, submittedAt].filter(Boolean).join(' ・ ')}</Text>
                          {showTooltip && (
                            <Tooltip label={t('evaluationHistoryDetailsCard.tooltip')}>
                              <Tooltip.Icon intent="secondary" />
                            </Tooltip>
                          )}
                        </Flex>
                      </DescriptionList.Details>

                      {evaluatedAt && (
                        <>
                          <DescriptionList.Term>
                            {t('evaluationHistoryDetailsCard.evaluated')}
                          </DescriptionList.Term>
                          <DescriptionList.Details>
                            {[evaluatedBy, evaluatedAt].filter(Boolean).join(' ・ ')}
                          </DescriptionList.Details>
                        </>
                      )}
                    </GridAlpha>
                  </DescriptionList>
                </Card>
                <RequirementEvaluationTable
                  requirement={requirement}
                  index={index}
                  disallowEvaluation
                  onDeleteAttachment={
                    canDeleteAttachments
                      ? attachmentId => {
                          const attachment = requirement.responses?.attachments?.find(
                            attachment => attachment.id === attachmentId,
                          );
                          assert(
                            attachment,
                            `Could not find attachment ${attachmentId} in requirement ${requirement.id}`,
                          );
                          setAttachmentToDelete(attachment);
                        }
                      : undefined
                  }
                />
              </Tabs.Item>
            );
          })}
        </Tabs>
        {attachmentToDelete && (
          <DeleteAttachmentDialog
            attachment={attachmentToDelete}
            isOpen={!!attachmentToDelete}
            requirement={requirement}
            onDeleteAttachment={({ id, isPii }) => {
              deleteAttachment({ id, isPii, onSuccess: () => setAttachmentToDelete(undefined) });
            }}
            onClose={() => setAttachmentToDelete(undefined)}
            isDeletingAttachment={isDeletingAttachment}
          />
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <Flex grow={1} justify="end" direction={{ xs: 'column', sm: 'row' }}>
          <Dialog.CloseButton emphasis="outlined" intent="primary">
            {t('evaluationHistoryModalClose')}
          </Dialog.CloseButton>
        </Flex>
      </Dialog.Actions>
    </Dialog>
  );
}

function EmptyAttachmentsState() {
  const { t } = useTranslation('ApplicationRequirementCard');
  const { permissions } = useApplicationPagePermissions();

  return (
    <Flex direction="column" gap={8}>
      <Heading level={4} variant="titleM">
        {t('documentPreview')}
      </Heading>
      <Flex direction="column" justify="center" align="center" gap={4}>
        <WomanThinkingIllustration />
        <Heading level={5}>
          {permissions.update_requirement_status
            ? t('emptyDocumentStateHeadingEvaluatorView')
            : t('emptyDocumentStateHeading')}
        </Heading>
        <Text>
          {permissions.update_requirement_status
            ? t('emptyAnswerOrDocumentStateMessageEvaluatorView')
            : t('emptyDocumentStateMessage')}
        </Text>
      </Flex>
    </Flex>
  );
}

function EmptyAnswersState() {
  const { t } = useTranslation('ApplicationRequirementCard');
  const { permissions } = useApplicationPagePermissions();

  return (
    <Flex direction="column" gap={8}>
      {/* TODO: did the mockups change? I don't see this `Heading` in the mock ups for empty state anymore */}
      <Heading level={4} variant="titleM">
        {t('questionnaireHeading')}
      </Heading>
      <Flex direction="column" justify="center" align="center" gap={4}>
        <WomanThinkingIllustration />
        <Heading level={5}>
          {permissions.update_requirement_status
            ? t('emptyAnswerStateHeadingEvaluatorView')
            : t('emptyAnswerStateHeading')}
        </Heading>
        <Text>
          {permissions.update_requirement_status
            ? t('emptyAnswerOrDocumentStateMessageEvaluatorView')
            : t('emptyAnswerStateMessage')}
        </Text>
      </Flex>
    </Flex>
  );
}

function NoAttachmentPreviewState() {
  const { t } = useTranslation('ApplicationRequirementCard');

  return (
    <Flex direction="column" gap={8} pt={2}>
      <Flex direction="column" justify="center" align="center" gap={4}>
        <WomanThinkingIllustration />
        <Text variant="titleM">{t('noDocumentPreview')}</Text>
        <Text>{t('noDocumentPreviewMessage')}</Text>
      </Flex>
    </Flex>
  );
}

function QuestionsPreview({ requirement }: { requirement: ApplicationRequirement }) {
  const { t } = useTranslation('ApplicationRequirementCard');

  assert(requirement.responses, 'Requirement must have responses');
  assert(requirement.responses.answersHistory, 'Requirement must have answers history');

  const formData = convertResponsesToDynamicForm(
    requirement.responses.answersHistory[0].answers.map(answer => {
      return { key: answer.key, value: answer.value };
    }),
  );
  const files =
    requirement.responses.attachments
      ?.filter(attachment => !attachment.deleted)
      ?.map(attachment => ({
        id: attachment.id,
        name: attachment.name,
        downloadLink: attachment.downloadLink,
        contentType: attachment.contentType,
        validationResults: [],
      })) || [];

  return (
    <Flex gap={4} direction="column">
      <Heading level={4} variant="titleM">
        {t('questionnaireHeading')}
      </Heading>
      <DynamicFormPreview
        formDefinition={requirement.formDefinition}
        formData={formData}
        files={files}
        showIncompleteFields={requirement.status === 'missing'}
      />
    </Flex>
  );
}

function AttachmentPreviewActionsButton({
  attachment,
  requirement,
  onDeleteAttachment,
  isDeletingAttachment,
}: {
  attachment: UserAttachment;
  requirement: ApplicationRequirement;
  onDeleteAttachment: ({ id, isPiiRemoval }: { id: string; isPiiRemoval?: boolean }) => void;
  isDeletingAttachment?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRemoveDocumentOpen, setIsRemoveDocumentOpen] = useState(false);
  const { t } = useTranslation('ApplicationRequirementCard');
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const { permissions } = useApplicationPagePermissions();
  const { trackEvent } = useRequirementListEventTracker();
  const attachmentComponent = getAttachmentComponent(requirement, attachment.id);

  if (!attachmentComponent || !attachmentComponent.properties) return null;

  const isReceipt = isReceiptComponent(attachmentComponent);
  const manageLink =
    'manageLink' in attachmentComponent.properties
      ? attachmentComponent.properties.manageLink
      : undefined;
  const canRemoveDocument =
    requirement.status === 'missing' || permissions['destroy_requirement_attachment_for_pii'];

  return (
    <>
      <MenuButton
        open={isOpen}
        onOpenChange={newOpen => {
          setIsOpen(newOpen);
          if (newOpen) {
            trackEvent({
              event: 'Document preview - Clicked three dot menu',
            });
          }
        }}
        alignment="right"
      >
        <MenuButton.Trigger>
          <Button
            leadIcon={MoreVertOutlineIcon}
            emphasis="outlined"
            intent="primary"
            size="sm"
            aria-label={t('documentPreviewActions', { documentName: attachment.name })}
            onClick={() => {
              setIsOpen(true);
            }}
            ref={menuButtonRef}
          />
        </MenuButton.Trigger>
        {canRemoveDocument && !isReceipt && (
          <MenuButton.Button
            icon={DeleteOutlineIcon}
            id={`deleteAttachmentButton-${attachment.id}`}
            onClick={() => {
              setIsRemoveDocumentOpen(true);
              trackEvent({
                event: 'Document preview - Three dot menu - Clicked remove document',
              });
            }}
          >
            {t('removeDocument.button')}
          </MenuButton.Button>
        )}
        <MenuButton.Link
          icon={DownloadOutlineIcon}
          href={`${window.location.origin}${attachment.downloadLink}`}
          download={attachment.name}
          id={`downloadAttachmentButton-${attachment.id}`}
        >
          {isReceipt ? t('downloadReceipt') : t('downloadDocument')}
        </MenuButton.Link>
        {manageLink && (
          <MenuButton.Link
            icon={ReceiptOutlineIcon}
            href={manageLink}
            id={`manageAttachmentButton-${attachment.id}`}
          >
            {t('manageOrder')}
          </MenuButton.Link>
        )}
      </MenuButton>
      <DeleteAttachmentDialog
        attachment={attachment}
        isOpen={isRemoveDocumentOpen}
        onClose={() => {
          flushSync(() => {
            setIsRemoveDocumentOpen(false);
          });
          menuButtonRef.current?.focus();
        }}
        requirement={requirement}
        onDeleteAttachment={onDeleteAttachment}
        isDeletingAttachment={isDeletingAttachment}
      />
    </>
  );
}

function DeleteAttachmentDialog({
  attachment,
  isOpen,
  onClose,
  requirement,
  onDeleteAttachment,
  isDeletingAttachment,
}: {
  attachment: UserAttachment;
  isOpen: boolean;
  onClose: () => void;
  requirement: ApplicationRequirement;
  onDeleteAttachment: ({ id, isPii }: { id: string; isPii?: boolean }) => void;
  isDeletingAttachment?: boolean;
}) {
  const { t } = useTranslation('ApplicationRequirementCard');
  const { permissions } = useApplicationPagePermissions();

  const canRemoveForPii = permissions['destroy_requirement_attachment_for_pii'];
  const [isPii, setIsPii] = useState(canRemoveForPii);
  const submittedAnswersWithAttachment =
    requirement.responses?.answersHistory?.filter(
      history =>
        history.submitted &&
        history.answers.some(
          answer => Array.isArray(answer.value) && answer.value.includes(attachment.id),
        ),
    ) || [];
  const isInvalidSelection = !isPii && submittedAnswersWithAttachment.length > 0;
  const mostRecentAnswerIndex = requirement.responses?.answersHistory?.findIndex(history =>
    history.answers.some(
      answer => Array.isArray(answer.value) && answer.value.includes(attachment.id),
    ),
  );
  const invalidSelectionHelpText =
    mostRecentAnswerIndex === 0
      ? t('removeDocument.piiDeleteOnlyHelpTextReviewing')
      : t('removeDocument.piiDeleteOnlyHelpTextEvaluated');

  return (
    <Dialog
      open={isOpen}
      onOpenChange={newOpen => {
        if (!newOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Heading>{t('removeDocument.title')}</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={4}>
          <Text>{t('removeDocument.info', { documentName: attachment.name })}</Text>
          {canRemoveForPii && (
            <Flex direction="column" gap={4}>
              <Text>{t('removeDocument.piiInstructions')}</Text>
              <RadioGroup
                label={t('removeDocument.piiLabel')}
                value={isPii ? 'yes' : 'no'}
                onChange={value => setIsPii(value === 'yes')}
                helpText={isInvalidSelection ? invalidSelectionHelpText : undefined}
                invalid={isInvalidSelection}
              >
                <Flex gap={4}>
                  <RadioGroup.Option value="yes" label={t('removeDocument.yes')} />
                  <RadioGroup.Option value="no" label={t('removeDocument.no')} />
                </Flex>
              </RadioGroup>
              <Notice intent="warning">
                {t(isPii ? 'removeDocument.piiNotice' : 'removeDocument.nonPiiNotice')}
              </Notice>
            </Flex>
          )}
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">{t('cancel')}</Dialog.CloseButton>
        <Button
          width="fill"
          intent="negative"
          emphasis="outlined"
          loading={isDeletingAttachment}
          onClick={() => {
            if (isInvalidSelection) {
              return;
            }
            onDeleteAttachment({ id: attachment.id, isPii });
          }}
        >
          {t('removeDocument.button')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
